/*
  <sort-popover> (snippets/sort-popover.liquid). A disclosure button with a list of sort options. The real
  <select name="sort_by"> inside it is hidden but stays in the form: choosing an option sets its value and
  fires `input`, which facets.js already listens for, so the grid re-renders as for the native control.
  The facets form does not re-render the sort control itself, so the label is updated here.
*/
if (!customElements.get('sort-popover')) {
  class SortPopover extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.sort-popover__trigger');
      this.panel = this.querySelector('.sort-popover__panel');
      this.select = this.querySelector('select');
      this.text = this.querySelector('.sort-popover__text');
      if (!this.trigger || !this.panel || !this.select) return;

      this.onOutsidePointer = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.onPopState = () => this.syncFromUrl();

      this.trigger.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
      this.panel.addEventListener('click', (event) => {
        const option = event.target.closest('.sort-popover__option');
        if (option) this.choose(option.dataset.value);
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('focusout', (event) => {
        if (this.isOpen() && !this.contains(event.relatedTarget)) this.close();
      });
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onOutsidePointer);
      window.removeEventListener('popstate', this.onPopState);
    }

    options() {
      return Array.from(this.panel.querySelectorAll('.sort-popover__option'));
    }

    isOpen() {
      return !this.panel.hidden;
    }

    open(focusTarget) {
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.onOutsidePointer);
      if (focusTarget === 'first' || focusTarget === 'last') {
        const options = this.options();
        (focusTarget === 'first' ? options[0] : options[options.length - 1])?.focus();
      }
    }

    close(returnFocus = false) {
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', this.onOutsidePointer);
      if (returnFocus) this.trigger.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen()) {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.target === this.trigger && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        this.open(event.key === 'ArrowDown' ? 'first' : 'last');
        return;
      }

      const options = this.options();
      const index = options.indexOf(document.activeElement);
      if (index === -1) return;

      let next = null;
      if (event.key === 'ArrowDown') next = options[(index + 1) % options.length];
      if (event.key === 'ArrowUp') next = options[(index - 1 + options.length) % options.length];
      if (event.key === 'Home') next = options[0];
      if (event.key === 'End') next = options[options.length - 1];
      if (next) {
        event.preventDefault();
        next.focus();
      }
    }

    choose(value) {
      this.apply(value);
      this.close(true);
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Sets the hidden select, the current marker and the trigger label. "Sort" alone for the default sort.
    apply(value) {
      this.select.value = value;
      let name = '';
      this.options().forEach((option) => {
        const current = option.dataset.value === value;
        if (current) {
          option.setAttribute('aria-current', 'true');
          name = option.textContent.trim();
        } else {
          option.removeAttribute('aria-current');
        }
      });
      const chosen = value !== this.dataset.defaultValue && name !== '';
      this.text.textContent = chosen ? `${this.dataset.label}: ${name}` : this.dataset.label;
    }

    // Back/forward changes the URL without re-rendering this control.
    syncFromUrl() {
      const value = new URLSearchParams(window.location.search).get('sort_by') || this.dataset.defaultValue;
      if (value !== this.select.value) this.apply(value);
    }
  }

  customElements.define('sort-popover', SortPopover);
}
